import {
  LinkedinFilled,
  LinkOutlined,
  PlusCircleFilled,
  MinusCircleOutlined,
  PlusCircleOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import {
  Button,
  ConfigProvider,
  Dropdown,
  Empty,
  Menu,
  Popover,
  Table,
  TablePaginationConfig,
  Typography,
} from 'antd';
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { FinancingNeed } from 'src/slices/financingNeed';
import { theme } from '../constants/theme';
import { Donor } from '../slices/donor';
import { OddImages } from './OddImages';
import { TagList } from './TagList';

const { SubMenu } = Menu;
const { Link, Text } = Typography;

interface DonorsTableProps {
  classification?: any;
  donors: Donor[] | null;
  updateDonorLoading: boolean;
  financingNeed?: FinancingNeed;
  financingNeeds: FinancingNeed[] | null;
  onTableChange?: (pagination: TablePaginationConfig) => void;
  onAddDonor: (donorId: string, dealFlowId: string) => void;
  onRemoveDonor: (donorId: string, dealFlowId: string) => void;
  onEditDonor?: (donorId: string) => void;
  loading: boolean;
  pagination: false | TablePaginationConfig | undefined;
  role?: string;
  callForTendersCounts?: { [key: string]: number };
}

const useStyles = createUseStyles({
  table: {
    maxWidth: '100%',
    padding: '1.15% 3.45%',
    tableLayout: 'fixed',
    '& .ant-table': {
      backgroundColor: 'transparent',
    },
    '& .ant-table table': {
      borderSpacing: '0 2.22vh',
      '& .ant-table-measure-row': {
        display: 'none',
      },
    },
    '& .ant-table-row': {
      backgroundColor: 'transparent',
      height: '11.92%',
    },
    '& .ant-table-cell-fix-left': {
      paddingLeft: '2.467% !important',
      paddingRight: '0px !important',
      borderTopLeftRadius: '2.029%',
      borderBottomLeftRadius: '2.029%',
      overflow: 'hidden',
    },
    '& .ant-table-cell-fix-right': {
      paddingLeft: '2.878% !important',
      paddingRight: '2.467% !important',
      verticalAlign: 'middle !important',
      borderTopRightRadius: '2.029%',
      borderBottomRightRadius: '2.029%',
    },
    '& .ant-table-cell': {
      height: '6vh',
      maxHeight: '6vh',
      verticalAlign: 'top',
      background: 'white',
      padding: '1.31% 1.64% 1.31% 2.47%',
    },
  },
  name: {
    fontWeight: '500',
    fontSize: '1vw',
    ['@media screen and (max-width: 950px)']: {
      fontSize: '11px',
    },
    cursor: 'pointer',
    fontFamily: "'Baloo 2'",
    textOverflow: 'ellipsis',
  },
  commonTags: {
    fontSize: '1.08vw',
    fontFamily: "'Baloo 2'",
    cursor: 'pointer',
  },
  socialsIcon: {
    color: theme.palette.primary,
    fontSize: '1.1875vw',
    margin: '0.625vw 1.25vw 0.625vw 0.25vw',
  },
  tags: {
    '& .tag-list': {
      fontSize: 0,
      margin: 0,
      '& .ant-tag': {
        margin: '0px 0.25vw 0px 0px',
        border: 'none',
        fontSize: '0.625vw',
        ['@media screen and (max-width: 1200px)']: {
          fontSize: '7.5px',
        },
        backgroundColor: 'transparent',
        padding: '0vw 0.3vw 0vw 0.3vw',
        lineHeight: 1.5715,
        marginBottom: '0.4vw',
        wordBreak: 'break-word',
        whiteSpace: 'unset',
      },
      '& .ant-typography': {
        fontSize: '0.625vw',
        lineHeight: 1.5715,
        display: 'inline list-item',
        margin: '0px 0.25vw 0px 0px',
      },
    },
  },
  textCallForTenders: {
    fontSize: '0.625vw',
    height: '10px',
    padding: 0,
    lineHeight: 1.5715,
    wordBreak: 'break-word',
    whiteSpace: 'unset',
    display: 'inline list-item',
    margin: 0,
  },
  descriptionText: {
    cursor: 'default',
  },
  descriptionPopup: {
    maxWidth: '25vw',
    maxHeight: '40vh',
    overflowY: 'auto',
  },
  subtitle: {
    color: theme.palette.secondary,
    fontWeight: 'bold',
    fontSize: '0.625vw',
    ['@media screen and (max-width: 1200px)']: {
      fontSize: '7.5px',
    },
  },
  plusButton: {
    height: 'auto',
    width: '1vw',
    marginRight: '3vw',
    marginLeft: '-21px',
    border: 'none',
    top: '4%',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    position: 'relative',
    '& .hover-icon': {
      visibility: 'hidden',
    },
    '&:hover .plus-icon': {
      display: 'none',
    },
    '&:hover .hover-icon': {
      visibility: 'visible',
    },
  },
});

export const DonorsTable: React.VFC<DonorsTableProps> = ({
  classification,
  donors,
  financingNeeds,
  financingNeed,
  onTableChange,
  onAddDonor,
  onRemoveDonor,
  onEditDonor,
  loading,
  pagination,
  role,
  callForTendersCounts,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const renderFinancingNeedsList = (
    financingNeedsOptions: FinancingNeed[],
    donorId: string,
    add: boolean,
  ) => (
    <>
      {financingNeedsOptions.map((financingNeedOption, index) => (
        <Menu.Item
          key={index}
          onClick={() => {
            add
              ? onAddDonor(donorId, financingNeedOption._id)
              : onRemoveDonor(donorId, financingNeedOption._id);
          }}
        >
          {financingNeedOption.name}
        </Menu.Item>
      ))}
    </>
  );

  const renderAddRemoveDonorToProjectMenu = (donor: Donor) =>
    financingNeed?.donors.includes(donor._id) ? (
      <Menu.Item
        onClick={() => {
          if (financingNeed) {
            onRemoveDonor(donor._id, financingNeed._id);
          }
        }}
      >
        {'Retirer des mécènes suivis'}
      </Menu.Item>
    ) : (
      <Menu.Item
        onClick={() => {
          if (financingNeed) {
            onAddDonor(donor._id, financingNeed._id);
          }
        }}
      >
        {'Ajouter aux mécènes suivis'}
      </Menu.Item>
    );

  const renderAddDonorSubMenu = (donor: Donor) => {
    const financingNeedsOptions = financingNeeds?.filter(
      (financingNeed) => !financingNeed.donors.includes(donor._id),
    );
    return (
      !!financingNeedsOptions?.length && (
        <SubMenu title={'Ajouter à un projet'}>
          {renderFinancingNeedsList(financingNeedsOptions, donor._id, true)}
        </SubMenu>
      )
    );
  };

  const rendeRemoveDonorSubMenu = (donor: Donor) => {
    const financingNeedsOptions = financingNeeds?.filter((financingNeed) =>
      financingNeed.donors.includes(donor._id),
    );
    return (
      !!financingNeedsOptions?.length && (
        <SubMenu title={"Retirer d'un projet"}>
          {renderFinancingNeedsList(financingNeedsOptions, donor._id, false)}
        </SubMenu>
      )
    );
  };

  const renderEditDonor = (donor: Donor) => {
    return (
      <Menu.Item onClick={() => onEditDonor && onEditDonor(donor._id)}>
        Modifier
      </Menu.Item>
    );
  };

  const renderSubTitle = (text: string) => (
    <Text className={classes.subtitle}>{text}</Text>
  );

  const calculateNbOfCallForTenders = (donorId?: string) => {
    if (callForTendersCounts && donorId) {
      const count = callForTendersCounts[donorId];
      return count || 0;
    }
    return 0;
  };

  const renderNbCallForTender = (nbCallForTenders: number) => {
    if (nbCallForTenders <= 0) {
      return 'Aucun programme de soutien en cours';
    } else if (nbCallForTenders === 1) {
      return '1 programme de soutien en cours';
    }
    return nbCallForTenders + ' programmes de soutien en cours';
  };

  const columns = useMemo(
    () =>
      classification
        ? [
            {
              title: 'Nom',
              dataIndex: 'name',
              key: '_id',
              width: '10.00%',
              fixed: 'left' as const,
              render: function renderName(name?: string, record?: Donor) {
                if (!record) return null;
                return (
                  <div>
                    <Popover
                      placement="topLeft"
                      content={
                        <div className={classes.descriptionPopup}>
                          <div className={classes.descriptionText}>
                            {record.description}
                          </div>
                        </div>
                      }
                    >
                      <div>
                        <div
                          className={classes.name}
                          onClick={() =>
                            history.push(
                              `/donor/${record._id}${
                                financingNeed
                                  ? `?projectId=${financingNeed._id}`
                                  : ''
                              }`,
                            )
                          }
                        >
                          {name}
                        </div>
                        {/* <div style={{ cursor: 'pointer' }}>
                            <EllipsisOutlined />
                          </div> */}
                      </div>
                    </Popover>
                  </div>
                );
              },
            },
            {
              title: 'Boutons',
              dataIndex: 'name',
              key: '_id',
              width: '10.00%',
              fixed: 'left' as const,
              render: function renderName(name?: string, record?: Donor) {
                if (!record) return null;
                const menu = (
                  <Menu>
                    {financingNeed && renderAddRemoveDonorToProjectMenu(record)}
                    {!financingNeed && renderAddDonorSubMenu(record)}
                    {!financingNeed && rendeRemoveDonorSubMenu(record)}
                    {role && role === 'admin' && renderEditDonor(record)}
                  </Menu>
                );
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderRight: 'solid',
                      borderRightColor: '#f6f5fa',
                      height: '6.69vw',
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      <div
                        style={{
                          display: 'flex',
                          marginLeft: 'auto',
                          justifyContent: 'space-between',
                        }}
                      >
                        {(record.homepageUrl || record.otherUrl) && (
                          <Link
                            href={record.homepageUrl ?? record.otherUrl}
                            target="_blank"
                          >
                            <LinkOutlined className={classes.socialsIcon} />
                          </Link>
                        )}
                        {record.linkedinUrl && (
                          <Link href={record.linkedinUrl} target="_blank">
                            <LinkedinFilled
                              className={classes.socialsIcon}
                              style={{ marginLeft: '-0.625vw' }}
                            />
                          </Link>
                        )}
                        {financingNeed && role !== 'admin' ? (
                          <Button
                            className={classes.plusButton}
                            onClick={() => {
                              if (financingNeed?.donors.includes(record._id)) {
                                onRemoveDonor(record._id, financingNeed._id);
                              } else {
                                onAddDonor(record._id, financingNeed._id);
                              }
                            }}
                            style={{
                              border: 'none',
                              backgroundColor: 'transparent',
                              boxShadow: 'none',
                            }}
                          >
                            <span className="plus-icon">
                              {financingNeed?.donors.includes(record._id) ? (
                                <CheckCircleFilled
                                  style={{
                                    fontSize: '1.25vw',
                                    color: theme.palette.primary,
                                  }}
                                />
                              ) : (
                                <PlusCircleOutlined
                                  style={{
                                    fontSize: '1.25vw',
                                    color: theme.palette.primary,
                                  }}
                                />
                              )}
                            </span>
                            <span className="hover-icon">
                              {financingNeed?.donors.includes(record._id) ? (
                                <MinusCircleOutlined
                                  style={{ fontSize: '1.25vw', color: 'red' }}
                                />
                              ) : (
                                <PlusCircleFilled
                                  style={{
                                    fontSize: '1.25vw',
                                    color: theme.palette.primary,
                                  }}
                                />
                              )}
                            </span>
                          </Button>
                        ) : (
                          <Dropdown overlay={menu}>
                            <Button
                              className={classes.plusButton}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                border: 'none',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                              }}
                            >
                              <span className="plus-icon">
                                {financingNeed?.donors.includes(record._id) ? (
                                  <CheckCircleFilled
                                    style={{
                                      fontSize: '1.25vw',
                                      color: theme.palette.primary,
                                    }}
                                  />
                                ) : (
                                  <PlusCircleOutlined
                                    style={{
                                      fontSize: '1.25vw',
                                      color: theme.palette.primary,
                                    }}
                                  />
                                )}
                              </span>
                              <span className="hover-icon">
                                {financingNeed?.donors.includes(record._id) ? (
                                  <MinusCircleOutlined
                                    style={{ fontSize: '1.25vw', color: 'red' }}
                                  />
                                ) : (
                                  <PlusCircleFilled
                                    style={{
                                      fontSize: '1.25vw',
                                      color: theme.palette.primary,
                                    }}
                                  />
                                )}
                              </span>
                            </Button>
                          </Dropdown>
                        )}
                      </div>
                    </div>
                  </div>
                );
              },
            },
            {
              title: 'Mots clés',
              dataIndex: 'tags',
              key: 'tags',
              width: '13.9%',
              render: function renderTags(tagIds: string[]) {
                return (
                  <div className={classes.tags}>
                    {renderSubTitle('Mots clés')}
                    <TagList
                      elementIds={tagIds ?? []}
                      maxElements={3}
                      collection={classification.tags}
                      color="default"
                      collectionName="tags"
                      highlightedIds={financingNeed?.tags ?? []}
                    />
                  </div>
                );
              },
            },
            {
              title: 'Statut',
              dataIndex: 'statuses',
              key: 'statuses',
              width: '10.62%',
              render: function renderTags(
                statusIds?: string[],
                record?: Donor,
              ) {
                return (
                  <div>
                    <div className={classes.tags}>
                      {renderSubTitle('Statut')}
                      {record?.status && (
                        <TagList
                          elementIds={[record?.status]}
                          maxElements={2}
                          collection={classification.statuses}
                          color="default"
                          collectionName="statuses"
                        />
                      )}
                      <TagList
                        elementIds={statusIds ?? []}
                        maxElements={2}
                        collection={classification.statuses}
                        color="default"
                        collectionName="statuses"
                      />
                    </div>
                  </div>
                );
              },
            },
            {
              title: 'Soutien orienté vers',
              dataIndex: 'targetPersons',
              key: 'targetPersons',
              width: '13.81%',
              render: function renderTags(
                targetPersonIds?: string[],
                record?: Donor,
              ) {
                return (
                  <div>
                    <div className={classes.tags}>
                      {renderSubTitle('Soutien orienté vers')}
                      {record?.targetPerson && (
                        <TagList
                          elementIds={
                            record?.targetPerson ? record?.targetPerson : []
                          }
                          maxElements={2}
                          collection={classification.targetPersons}
                          color="default"
                          collectionName="targetPersons"
                        />
                      )}
                      <TagList
                        elementIds={targetPersonIds ?? []}
                        maxElements={2}
                        collection={classification.targetPersons}
                        color="default"
                        collectionName="targetPersons"
                      />
                    </div>
                    <div className={classes.tags}>
                      {renderSubTitle("Domaines d'activité")}
                      <TagList
                        elementIds={record?.activityDomains ?? []}
                        maxElements={2}
                        collection={classification.activityDomains}
                        color="default"
                        collectionName="activityDomains"
                      />
                    </div>
                  </div>
                );
              },
            },
            {
              title: "Zones d'activité",
              dataIndex: 'activityZones',
              key: 'activityZones',
              width: '14.4%',
              render: function renderTags(tagIds?: string[], record?: Donor) {
                return (
                  <div className={classes.tags ?? []}>
                    {renderSubTitle("Zones d'activité")}
                    <TagList
                      elementIds={tagIds ?? []}
                      maxElements={3}
                      collection={classification.activityZones}
                      color="default"
                      collectionName="activityZones"
                    />
                    {renderSubTitle('Bénéficiaires cibles')}
                    <TagList
                      elementIds={record?.targetPopulations ?? []}
                      collection={classification.targetPopulations}
                      maxElements={3}
                      color="default"
                      collectionName="targetPopulations"
                    />
                  </div>
                );
              },
            },
            {
              title: 'Type de mécénat',
              dataIndex: 'donationTypes',
              key: 'donationTypes',
              width: '15.215%',
              render: function renderTags(tagIds?: string[], record?: Donor) {
                const nbCallForTenders = calculateNbOfCallForTenders(
                  record?._id,
                );
                return (
                  <div className={classes.tags}>
                    {renderSubTitle('Type de mécénat')}
                    <TagList
                      elementIds={tagIds ?? []}
                      collection={classification.donationTypes}
                      maxElements={3}
                      color="default"
                      collectionName="donationTypes"
                    />
                    {
                      <div>
                        {/*{renderSubTitle('Appels à projets')}*/}
                        {renderSubTitle('Programmes de soutien')}
                        <br />
                        <div className={classes.textCallForTenders}>
                          {renderNbCallForTender(nbCallForTenders)}
                        </div>
                      </div>
                    }
                  </div>
                );
              },
            },
            {
              title: 'ODD',
              dataIndex: 'sustainableDevelopmentGoals',
              key: 'sustainableDevelopmentGoals',
              width: '10.69%',
              render: function renderSDG(sdgIds?: string[]) {
                return (
                  <div className={classes.tags} style={{ maxWidth: '10vw' }}>
                    {renderSubTitle('ODD')}
                    {/*<TagList*/}
                    {/*  elementIds={sdgIds ?? []}*/}
                    {/*  collection={classification.sustainableDevelopmentGoals}*/}
                    {/*  maxElements={4}*/}
                    {/*  color="default"*/}
                    {/*  collectionName="sustainableDevelopmentGoals"*/}
                    {/*/>*/}
                    <OddImages
                      sustainableDevelopmentGoals={
                        classification.sustainableDevelopmentGoals
                      }
                      oddIds={sdgIds ?? []}
                      size={25}
                    />
                  </div>
                );
              },
            },
          ]
        : [],
    [classification, financingNeeds],
  );

  return (
    <ConfigProvider
      renderEmpty={() => (
        <Empty description={`Aucun résultat pour le moment...`} />
      )}
    >
      <Table
        className={classes.table}
        dataSource={donors ?? undefined}
        columns={columns}
        rowKey="_id"
        onChange={onTableChange}
        loading={loading || !donors || !classification}
        pagination={pagination}
        // scroll={{ x: true }}
        showHeader={false}
      />
    </ConfigProvider>
  );
};
